import type { Project, ProjectStatus } from '@builder/shared';
import { Card, CardBody } from '@/components/ui/Card';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

interface ProjectsOverviewProps {
  projects: Pick<Project, 'status' | 'progressPercent' | 'estimatedEndDate' | 'actualEndDate'>[];
  className?: string;
}

/**
 * Compact at-a-glance strip for the projects list. Shows four numbers:
 * active, on hold, completed, and overdue. The numbers animate in with a
 * subtle stagger — small detail, but it makes returning to the list feel
 * alive without pulling focus.
 */
export function ProjectsOverview({ projects, className }: ProjectsOverviewProps) {
  const { t } = useT();
  const stats = computeStats(projects);

  const tiles: Array<{ label: string; value: number; tone: Tone }> = [
    { label: t.projects.stats.inProgress, value: stats.inProgress, tone: 'brand' },
    { label: t.projects.stats.onHold, value: stats.onHold, tone: 'warn' },
    { label: t.projects.stats.completed, value: stats.completed, tone: 'ok' },
    { label: t.projects.stats.overdue, value: stats.overdue, tone: 'err' },
  ];

  return (
    <Card className={className}>
      <CardBody className="grid grid-cols-4 divide-x divide-line px-0 py-0">
        {tiles.map((tile, i) => (
          <div
            key={tile.label}
            className="px-3 py-3.5 text-center animate-fade-in-up"
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <p className={cn('text-2xl font-semibold tabular-nums tracking-tight', toneToText(tile.tone))}>
              {tile.value}
            </p>
            <p className="mt-0.5 text-[11px] uppercase tracking-wider text-ink-subtle">
              {tile.label}
            </p>
          </div>
        ))}
      </CardBody>
    </Card>
  );
}

type Tone = 'brand' | 'warn' | 'ok' | 'err';

function toneToText(tone: Tone): string {
  switch (tone) {
    case 'brand':
      return 'text-ink';
    case 'warn':
      return 'text-warn';
    case 'ok':
      return 'text-ok';
    case 'err':
      return 'text-err';
  }
}

function computeStats(
  projects: Pick<Project, 'status' | 'progressPercent' | 'estimatedEndDate' | 'actualEndDate'>[],
) {
  const now = Date.now();
  let inProgress = 0;
  let onHold = 0;
  let completed = 0;
  let overdue = 0;

  for (const p of projects) {
    if (p.status === 'in_progress' || p.status === 'planning') inProgress += 1;
    else if (p.status === 'on_hold') onHold += 1;
    else if (p.status === 'completed') completed += 1;

    if (
      p.status !== 'completed' &&
      p.status !== 'archived' &&
      p.estimatedEndDate &&
      new Date(p.estimatedEndDate).getTime() < now
    ) {
      overdue += 1;
    }
  }
  return { inProgress, onHold, completed, overdue };
}

const STATUS_ORDER: ProjectStatus[] = [
  'in_progress',
  'planning',
  'on_hold',
  'completed',
  'archived',
];

/** Sort helper shared by the list page — keeps active work at the top. */
export function sortProjectsForList<T extends Pick<Project, 'status' | 'updatedAt'>>(
  projects: T[],
): T[] {
  return [...projects].sort((a, b) => {
    const sa = STATUS_ORDER.indexOf(a.status);
    const sb = STATUS_ORDER.indexOf(b.status);
    if (sa !== sb) return sa - sb;
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}
